"use client";

import React from "react";
import { alpha, Box } from "@mui/material";
import { TravelStylesPage } from "@/types/dto/travel-styles/travel-styles";
import CommonInterLinking from "@/components/common/common-interlinking";
import FeaturedTours from "@/components/common/featured-tours";
import BasePageHero from "../common/page-hero/base-page-hero/base-page-hero";
import CardGridSection from "./card-grid-section/card-grid-section";
import { UnforgettableMomentsComponent } from "../landing-page/unforgettable-moments/unforgettable-moments";
import InterlinkingMenu from "../landing-page/interlinking-menu/interlinking-menu";
import TextBlocks from "./text-blocks";
import NeedMoreInfo from "../common/need-more-info/need-more-info";
import FaqSectionComponent from "../common/faq/faq";
import RelatedContentSectionLayout from "../destinations-page/related-content/related-content-section";
import PageHero from "../common/page-hero/page-hero";
import createVideoSectionSlotProps from "./create-video-section-slot-props";

const TravelStylesLayout = ({
  heroSection,
  textBlocks,
  cardGrid,
  unforgettableMoments,
  relatedContentSection,
  formSection,
  interlinkSection,
  featuredTours,
  faqSection,
  otherTravelStyles,
  videoSection
}: TravelStylesPage) => {
  let rank = 0;
  const nextRank = () => {
    rank += 1;
    return rank;
  };
  
  const videoSectionSlotProps = createVideoSectionSlotProps({
    mediaContainer: {
      sx: {
        maxWidth: {
          lg: "640px"
        }
      }
    }
  });

  return (
    <Box>
      {heroSection && heroSection?.backgroundImage?.imageUrl && heroSection.basicInfo && (
        <BasePageHero
          dataModuleName="Travel Styles Header"
          moduleRankingPosition={String(nextRank())}
          dataParentContainerType="page hero"
          imageSrc={heroSection.backgroundImage.imageUrl}
          imageAlt={heroSection.backgroundImage.altText || "Travel Styles header background image"}
          pageTitle={heroSection.basicInfo.headerText || "Travel Styles Default Heading"}
          basicInfoMultipleCtas={heroSection.basicInfo}
          variant="text"
          slotProps={{
            container: {
              sx: { minHeight: { xs: "45rem", md: "45rem", lg: "60rem" } }
            },
            backgroundImage: {
              sx: {
                background: (theme) =>
                  `linear-gradient(0deg, ${alpha(theme.palette.common.black, 0.6)} 0%, ${alpha(theme.palette.common.black, 0)} 100%)`
              }
            }
          }}
        />
      )}

      {textBlocks && textBlocks.length > 0 && (
        <TextBlocks
          textBlocks={textBlocks}
          moduleRankingPosition={String(nextRank())}
          parentContainerType="text block"
        />
      )}

      {cardGrid && (
        <CardGridSection
          cardGrid={cardGrid}
          moduleRanking={nextRank()}
          moduleName="Card Grid"
          parentContainerType="card grid"
        />
      )}

      {videoSection && videoSection.basicInfo && (
        <PageHero
          testId="video-section"
          heroBasicInfo={videoSection.basicInfo}
          headerImage={videoSection.backgroundImage}
          video={
            videoSection.video
              ? {
                  videoId: videoSection.video.videoId,
                  videoTitle: videoSection.video.title
                }
              : undefined
          }
          padding={{
            mobile: "4rem 2.4rem 2rem 2.4rem",
            tablet: "4rem 3.2rem",
            desktop: "6rem 10rem 3rem 10rem"
          }}
          moduleRankingPosition={String(nextRank())}
          mediaLeft={videoSection.design === "Media Left"}
          slotProps={videoSectionSlotProps}
          headerLevel="h2"
          dataModuleName="Media Text and CTA"
        />
      )}

      {unforgettableMoments && (
        <UnforgettableMomentsComponent
          moments={unforgettableMoments}
          moduleRankingPosition={String(nextRank())}
          containerSx={{ p: { xs: "4rem 2.4rem", sm: "4rem 3.2rem", lg: "6rem 10rem" } }}
          headerGap={{ paddingBottom: "4rem" }}
        />
      )}

      {formSection && (
        <NeedMoreInfo
          needMoreInfoData={{ ...formSection, customBg: "pattern-bg", isFullWidth: true }}
          moduleRankingPosition={String(nextRank())}
          parentContainerType="form"
        />
      )}

      {interlinkSection && (
        <InterlinkingMenu destinations={interlinkSection} moduleRankingPosition={String(nextRank())} arrowIconFlag />
      )}

      {featuredTours && (
        <FeaturedTours
          tourCarousel={featuredTours}
          showRating
          moduleRankingPosition={String(nextRank())}
          dataRankingAlgorithm="algorithmic"
          padding={{
            tablet: "4rem 3.2rem",
            desktop: "6rem 10rem",
            wide: "6rem 10rem"
          }}
        />
      )}

      {relatedContentSection && (
        <RelatedContentSectionLayout
          relatedContentSection={relatedContentSection}
          moduleRankingPosition={nextRank()}
          centerHeader
        />
      )}
      {faqSection && (
        <FaqSectionComponent
          faqSection={faqSection}
          headerSx={{
            textTransform: "none",
            fontSize: { xs: "2.4rem", sm: "2.6rem", lg: "2.8rem" }
          }}
          moduleRankingPosition={String(nextRank())}
          headerComponent="h3"
          faqContainerSx={{ px: { xs: "2.4rem", sm: "3.2rem", lg: "10rem" } }}
        />
      )}
      {otherTravelStyles && otherTravelStyles.length > 0 && (
        <CommonInterLinking
          dataModuleRanking={nextRank()}
          interlinkData={otherTravelStyles.map((item) => ({
            headerText: item.headerText || "",
            callToActionUrl: item.callToActionUrl || ""
          }))}
          headingText="Explore Other Travel Styles"
          ariaLabel="Other travel styles navigation"
          testIdPrefix="travel-styles"
          parentContainerType="travel-styles-interlinking"
          moduleName="Other Travel Styles"
        />
      )}
    </Box>
  );
};

export default TravelStylesLayout;
